import { head, last, without } from "lodash";
import { Metric, getAllMetrics } from "@linkfluence/models/metric";
import {
  isBubbleChartDataviz,
  isHorizontalBarChartDataviz,
  isKpiDataviz,
} from "../dataviz";
import { GeoTerm, getTermFromAggs } from "../search";
import {
  isClassificationCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isGeoCard,
  isPivotCard,
  isTermCard,
  isTermMetricsCard,
} from "./is";
import { Card } from "./model";

export function getMetricsFromCard(card: Card) {
  if (isClassificationCard(card)) {
    const [fragment] = card.fragments;
    return fragment.request.aggs.contentClassification.metrics;
  } else if (isGeoCard(card)) {
    const [fragment] = card.fragments;
    const term = getTermFromAggs(fragment.request.aggs) as GeoTerm;
    return fragment.request.aggs[term].metrics;
  } else if (isTermCard(card) || isTermMetricsCard(card)) {
    const [fragment] = card.fragments;
    const term = getTermFromAggs(fragment.request.aggs);
    return fragment.request.aggs[term].metrics;
  } else if (
    isDateTermCard(card) ||
    isDateMetricCard(card) ||
    isDateMetricsCard(card)
  ) {
    const [fragment] = card.fragments;
    return fragment.request.aggs.date.metrics;
  } else if (isPivotCard(card)) {
    const [fragment] = card.fragments;
    const term = getTermFromAggs(fragment.request.aggs);
    return fragment.request.aggs[term].metrics;
  } else if (isDemographicCard(card)) {
    const [fragment] = card.fragments;
    return fragment.request.aggs.genders.metrics;
  } else {
    return [Metric.Doc];
  }
}

export function getComparaisonMetricsFromCard(card: Card) {
  if (isClassificationCard(card)) {
    if (card.fragments[1]) {
      return card.fragments[1].request.aggs.contentClassification.metrics;
    } else {
      return [Metric.Doc];
    }
  } else if (isGeoCard(card)) {
    if (card.fragments[1]) {
      const term = getTermFromAggs(card.fragments[1].request.aggs);
      return card.fragments[1].request.aggs[term as GeoTerm].metrics;
    } else {
      return [Metric.Doc];
    }
  } else if (isTermCard(card) || isTermMetricsCard(card)) {
    if (card.fragments[1]) {
      const term = getTermFromAggs(card.fragments[1].request.aggs);
      return card.fragments[1].request.aggs[term].metrics;
    } else {
      return [Metric.Doc];
    }
  } else if (isDateTermCard(card) || isDateMetricCard(card)) {
    if (card.fragments[1]) {
      return card.fragments[1].request.aggs.date.metrics;
    } else {
      return [Metric.Doc];
    }
  } else if (isPivotCard(card)) {
    return [Metric.Doc];
  } else if (isDemographicCard(card)) {
    return [Metric.Doc];
  } else {
    return [Metric.Doc];
  }
}

function getPrimaryMetricFromCardAndMetrics(card: Card, metrics: Metric[]) {
  if (isTermMetricsCard(card) && isBubbleChartDataviz(card.dataviz)) {
    return last(metrics);
  } else {
    return head(metrics);
  }
}

function getSecondaryMetricFromCardAndMetrics(card: Card, metrics: Metric[]) {
  if (isBubbleChartDataviz(card.dataviz)) {
    return metrics[0];
  } else {
    return metrics[1];
  }
}

function getTertiaryMetricFromCardAndMetrics(card: Card, metrics: Metric[]) {
  if (isBubbleChartDataviz(card.dataviz)) {
    return metrics[1];
  } else {
    return last(metrics);
  }
}

export function getPrimaryMetricFromCard(card: Card) {
  return getPrimaryMetricFromCardAndMetrics(card, getMetricsFromCard(card));
}

export function getSecondaryMetricFromCard(card: Card) {
  return getSecondaryMetricFromCardAndMetrics(card, getMetricsFromCard(card));
}

export function getTertiaryMetricFromCard(card: Card) {
  return getTertiaryMetricFromCardAndMetrics(card, getMetricsFromCard(card));
}

export function getComparaisonMetricFromCard(card: Card) {
  return getPrimaryMetricFromCardAndMetrics(
    card,
    getComparaisonMetricsFromCard(card)
  );
}

export function getAvailableMetrics() {
  return without(getAllMetrics(), Metric.Rating, Metric.Impression);
}

export function getAvailableMetricsFromCard(card: Card) {
  const metrics = getAllMetrics();
  if (isHorizontalBarChartDataviz(card.dataviz)) {
    return without(metrics, Metric.Impression, Metric.NetSentiment);
  }
  if (isKpiDataviz(card.dataviz)) {
    return without(metrics, Metric.Rating, Metric.Impression);
  }
  return without(
    metrics,
    Metric.Rating,
    Metric.Impression,
    Metric.NetSentiment,
    Metric.EngagementPerPost
  );
}

export function hasMetricsAggsFromCard(card: Card) {
  if (
    isTermCard(card) ||
    isGeoCard(card) ||
    isClassificationCard(card) ||
    isTermMetricsCard(card) ||
    isPivotCard(card) ||
    isDateMetricCard(card) ||
    isDateMetricsCard(card) ||
    isDateTermCard(card) ||
    isDemographicCard(card)
  ) {
    const [fragment] = card.fragments;
    return !!fragment.request.aggs.metrics;
  }
  return false;
}
